import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Calendar, Music, Loader2, Flame, Zap } from 'lucide-react';

type LeaderboardType = 'global' | 'weekly' | 'monthly' | 'genre';

interface LeaderboardPlayer {
  rank: number;
  id: number;
  name: string;
  avatar: string | null;
  points: number;
  gamesWon: number;
  titlesFound?: number;
  streak?: number;
}

// Genres disponibles pour le filtre
const GENRES = [
  { id: 'all', name: 'Tous', icon: '🎵' },
  { id: 'pop', name: 'Pop', icon: '🎤' },
  { id: 'rock', name: 'Rock', icon: '🎸' },
  { id: 'hiphop', name: 'Hip-Hop', icon: '🎧' },
  { id: 'electronic', name: 'Électro', icon: '🎹' },
  { id: 'french', name: 'Français', icon: '🇫🇷' },
  { id: 'classics', name: 'Classiques', icon: '📻' },
];

export default function LeaderboardPage() {
  const [activeTab, setActiveTab] = useState<LeaderboardType>('global');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [leaderboard, setLeaderboard] = useState<LeaderboardPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Charger le classement depuis l'API
  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      setError(null);
      try {
        const genreParam = activeTab === 'genre' && selectedGenre !== 'all' ? `&genre=${selectedGenre}` : '';
        const response = await fetch(`/api/leaderboard?type=${activeTab}&limit=50${genreParam}`);
        if (response.ok) {
          const data = await response.json();
          // Si le serveur renvoie des données
          if (data.leaderboard && data.leaderboard.length > 0) {
            setLeaderboard(data.leaderboard);
          } else {
            // Pas de données, ce n'est pas une erreur
            setLeaderboard([]);
          }
        } else {
          // En cas d'erreur API, on pourrait afficher des données mock
          setLeaderboard([]);
        }
      } catch (err) {
        console.error('Erreur fetch leaderboard:', err);
        // Afficher un message mais ne pas bloquer l'affichage
        setLeaderboard([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeaderboard();
  }, [activeTab, selectedGenre]);
  
  const tabs = [
    { id: 'global', label: 'Global', icon: <Trophy className="w-4 h-4" /> },
    { id: 'weekly', label: 'Semaine', icon: <Flame className="w-4 h-4" /> },
    { id: 'monthly', label: 'Mois', icon: <Calendar className="w-4 h-4" /> },
    { id: 'genre', label: 'Par genre', icon: <Music className="w-4 h-4" /> },
  ];
  
  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="font-display text-3xl font-bold mb-2 flex items-center gap-3">
          <Trophy className="w-8 h-8 text-yellow-500" />
          Classement
        </h1>
        <p className="text-dark-400 mb-8">
          Les meilleurs joueurs du BlindTest !
        </p>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as LeaderboardType)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-white'
                  : 'bg-white/5 text-dark-300 hover:bg-white/10'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Genre Filter - affiché uniquement quand l'onglet "genre" est actif */}
        {activeTab === 'genre' && (
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {GENRES.map((genre) => (
              <button
                key={genre.id}
                onClick={() => setSelectedGenre(genre.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  selectedGenre === genre.id
                    ? 'bg-secondary-500 text-white'
                    : 'bg-white/5 text-dark-300 hover:bg-white/10'
                }`}
              >
                <span>{genre.icon}</span>
                {genre.name}
              </button>
            ))}
          </div>
        )}
        
        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
            <span className="ml-3 text-dark-400">Chargement du classement...</span>
          </div>
        )}
        
        {/* Error */}
        {error && !loading && (
          <div className="card text-center py-8">
            <p className="text-red-400">{error}</p>
          </div>
        )}
        
        {/* Podium */}
        {!loading && !error && leaderboard.length > 0 && (
        <div className="flex justify-center items-end gap-4 mb-8 py-8">
          {/* 2ème */}
          {leaderboard[1] && (
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {leaderboard[1].avatar ? (
                <img src={leaderboard[1].avatar} alt={leaderboard[1].name} className="w-20 h-20 mx-auto mb-2 rounded-full object-cover border-4 border-gray-400" />
              ) : (
                <div className="w-20 h-20 mx-auto mb-2 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center text-2xl font-bold text-dark-900">
                  {leaderboard[1].name.charAt(0)}
                </div>
              )}
              <p className="font-semibold">{leaderboard[1].name}</p>
              <p className="text-primary-400 text-sm">{leaderboard[1].points.toLocaleString()} pts</p>
            </motion.div>
          )}
          
          {/* 1er */}
          {leaderboard[0] && (
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="relative">
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-3xl">
                  👑
                </div>
                {leaderboard[0].avatar ? (
                  <img src={leaderboard[0].avatar} alt={leaderboard[0].name} className="w-28 h-28 mx-auto mb-2 rounded-full object-cover border-4 border-yellow-500 shadow-glow" />
                ) : (
                  <div className="w-28 h-28 mx-auto mb-2 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-4xl font-bold text-dark-900 shadow-glow">
                    {leaderboard[0].name.charAt(0)}
                  </div>
                )}
              </div>
              <p className="font-bold text-lg">{leaderboard[0].name}</p>
              <p className="text-primary-400 font-semibold">{leaderboard[0].points.toLocaleString()} pts</p>
            </motion.div>
          )}
          
          {/* 3ème */}
          {leaderboard[2] && (
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {leaderboard[2].avatar ? (
                <img src={leaderboard[2].avatar} alt={leaderboard[2].name} className="w-20 h-20 mx-auto mb-2 rounded-full object-cover border-4 border-orange-500" />
              ) : (
                <div className="w-20 h-20 mx-auto mb-2 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-2xl font-bold text-dark-900">
                  {leaderboard[2].name.charAt(0)}
                </div>
              )}
              <p className="font-semibold">{leaderboard[2].name}</p>
              <p className="text-primary-400 text-sm">{leaderboard[2].points.toLocaleString()} pts</p>
            </motion.div>
          )}
        </div>
        )}
        
        {/* Liste complète */}
        {!loading && !error && leaderboard.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4 text-sm text-dark-400 px-4">
            <span>Rang</span>
            <span className="flex-1 ml-4">Joueur</span>
            <span className="w-24 text-right">Points</span>
            <span className="w-24 text-right hidden md:block">Trouvés</span>
          </div>
          
          <div className="space-y-2">
            {leaderboard.map((player, index) => (
              <motion.div
                key={player.id || player.rank}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: Math.min(index * 0.03, 0.5) }}
                className={`flex items-center p-4 rounded-lg ${
                  index < 3 ? 'bg-primary-500/10' : 'bg-white/5'
                } hover:bg-white/10 transition-colors`}
              >
                <span className={`w-8 font-bold ${
                  index === 0 ? 'text-yellow-500' :
                  index === 1 ? 'text-gray-400' :
                  index === 2 ? 'text-orange-500' :
                  'text-dark-400'
                }`}>
                  {player.rank}
                </span>
                
                <div className="flex items-center gap-3 flex-1">
                  {player.avatar ? (
                    <img src={player.avatar} alt={player.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center font-bold">
                      {player.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="font-medium">{player.name}</span>
                </div>
                
                <span className="w-24 text-right font-semibold text-primary-400">
                  {player.points.toLocaleString()}
                </span>
                
                <span className="w-24 text-right text-dark-400 hidden md:flex items-center justify-end gap-1">
                  <Trophy className="w-4 h-4" />
                  {player.gamesWon}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
        )}
        
        {/* Message si liste vide */}
        {!loading && !error && leaderboard.length === 0 && (
          <div className="card text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary-500/20 to-secondary-500/20 flex items-center justify-center">
              <Trophy className="w-10 h-10 text-primary-400" />
            </div>
            <h3 className="text-xl font-bold mb-2">Le classement se construit !</h3>
            <p className="text-dark-400 mb-4">Jouez des parties pour apparaître ici</p>
            <p className="text-dark-500 text-sm">
              {activeTab === 'weekly' && "Les scores de la semaine sont remis à zéro chaque lundi"}
              {activeTab === 'monthly' && "Les scores du mois sont remis à zéro le 1er de chaque mois"}
              {activeTab === 'genre' && selectedGenre !== 'all' && `Aucun score enregistré en ${GENRES.find(g => g.id === selectedGenre)?.name || 'ce genre'}`}
              {activeTab === 'global' && "Soyez le premier à marquer des points !"}
            </p>
            <button 
              onClick={() => window.location.href = '/'}
              className="btn-primary mt-6 inline-flex items-center gap-2"
            >
              <Zap className="w-5 h-5" />
              Jouer maintenant
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
